import type { RefObject } from 'react';
import { TaskAudioPlayer, TaskItemCard, TaskItemField } from '@/features/contribution-kit';
import type { TranscriptionAudioCopy } from './transcription-audio-copy';
import type { AudioEntry } from './types';

type TranscriptionAudioSourceCardProps = {
  t: Record<string, unknown>;
  copy: TranscriptionAudioCopy;
  current: AudioEntry;
  isPlaying: boolean;
  audioRef: RefObject<HTMLAudioElement>;
  canvasRef: RefObject<HTMLCanvasElement>;
  onPlayAudio: () => void;
  onAudioEnded: () => void;
};

const audioType = (url: string) => (url.endsWith('.webm') ? 'audio/webm' : url.endsWith('.ogg') ? 'audio/ogg' : 'audio/wav');

/**
 * The audio to transcribe: its waveform player and what is known of it (the
 * French phrase and its local equivalent, or the question an initial-data
 * answer replies to).
 */
export function TranscriptionAudioSourceCard({
  t,
  copy,
  current,
  isPlaying,
  audioRef,
  canvasRef,
  onPlayAudio,
  onAudioEnded,
}: TranscriptionAudioSourceCardProps) {
  const s = (key: string) => String(t[key] ?? '');
  return (
    <TaskItemCard label={copy.sourceLabel}>
      <TaskAudioPlayer
        isPlaying={isPlaying}
        onToggle={onPlayAudio}
        canvasRef={canvasRef}
        playLabel={s('playAudio')}
        pauseLabel={s('pauseAudio')}
      />
      <audio key={current.id} ref={audioRef} onEnded={onAudioEnded} className="hidden">
        <source src={current.audioUrl} type={audioType(current.audioUrl)} />
      </audio>

      {current.isInitialAnswer ? (
        // A free answer in the local language: showing the question as
        // "French" made transcribers translate it instead.
        <TaskItemField label={s('answerToQuestion')} testId="transcription-answer-to-question">
          <p className="font-medium">{current.langFr || copy.notProvided}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{s('answerToQuestionHint')}</p>
        </TaskItemField>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <TaskItemField label={s('french')} testId="transcription-audio-french">
            {current.langFr || copy.notProvided}
          </TaskItemField>
          <TaskItemField label={s('equivalent')} testId="transcription-audio-equivalent">
            {current.word || current.transcription || copy.notProvided}
          </TaskItemField>
        </div>
      )}
    </TaskItemCard>
  );
}
